import type { AxiosInstance, AxiosResponse } from 'axios';
import qs from 'qs';
import type { ApiResponse } from '@/types/api';

/**
 * Generic API Factory for creating services that interact with API endpoints.
 *
 * This class abstracts common HTTP methods (GET, POST, PATCH, DELETE) and
 * uses the provided Axios instance for making requests.
 *
 * @template T - The type of the resource the service will handle.
 */
export class ApiFactory<T> {
    protected resource: string;
    protected api: AxiosInstance;

    /**
     * Creates an instance of ApiFactory.
     * @param resource - The base resource path (e.g., '/branches', '/carts')
     * @param apiInstance - The configured Axios instance
     */
    constructor(resource: string, apiInstance: AxiosInstance) {
        this.resource = resource;
        this.api = apiInstance;
    }

    /**
     * Serializes an object into a URL query string.
     * @param params - The object to serialize.
     * @returns A URL-encoded query string.
     */
    private serializeParams(params: Record<string, any>): string {
        return qs.stringify(params, { arrayFormat: 'brackets' });
    }

    /**
     * Executes a GET request.
     * @param endpoint - The endpoint path (can be full path or relative to resource)
     * @param params - Optional query parameters
     * @param config - Optional axios config
     * @returns Promise with the response data
     */
    protected async get<R = ApiResponse<T>>(
        endpoint: string,
        params?: Record<string, any>,
        config?: Record<string, any>,
    ): Promise<R> {
        const query = params ? `?${this.serializeParams(params)}` : '';
        const url = `${endpoint}${query}`;
        const response: AxiosResponse<R> = await this.api.get(`${endpoint}${query}`, config);
        return response.data;
    }

    /**
     * Executes a POST request.
     * @param endpoint - The endpoint path
     * @param body - The request payload
     * @param config - Optional axios config
     * @returns Promise with the response data
     */
    protected async post<R = ApiResponse<T>>(
        endpoint: string,
        body?: Record<string, any>,
        config?: Record<string, any>,
    ): Promise<R> {
        const response: AxiosResponse<R> = await this.api.post(endpoint, body, config);
        return response.data;
    }

    /**
     * Executes a PATCH request.
     * @param endpoint - The endpoint path
     * @param body - The request payload
     * @param config - Optional axios config
     * @returns Promise with the response data
     */
    protected async patch<R = ApiResponse<T>>(
        endpoint: string,
        body: Record<string, any>,
        config?: Record<string, any>,
    ): Promise<R> {
        const response: AxiosResponse<R> = await this.api.patch(endpoint, body, config);
        return response.data;
    }

    /**
     * Executes a DELETE request.
     * @param endpoint - The endpoint path
     * @param body - Optional request payload
     * @returns Promise with the response data
     */
    protected async delete<R = ApiResponse<null>>(
        endpoint: string,
        body?: Record<string, any>,
    ): Promise<R> {
        const response: AxiosResponse<R> = await this.api.delete(endpoint, {
            data: body,
        });
        return response.data;
    }
}
